import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';

import { AutoCompleteSelectUI, ButtonUI, InputUI, PhoneBase } from '../Interface';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import useUsers from '../../context/UsersContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import { DocumentType } from '../../config/DocumentType';
import cooperationMaterialService from '../../service/cooperationMaterial.service';
import companyService from '../../service/company.service';
import documentTypeService from '../../service/newDocumentType.service';
import itemService from '../../service/item.service';
import masterDataService from '../../service/masterData.service';
import { useLoader } from '../../provider/LoaderProvider';
import userService from '../../service/user.service';
import PinCodeLogInDialog from '../Interface/PinCodeLogInDialog';
import { AddressType } from '../../config/company';
import { DocLineStatus, DocStatus } from '../../config/document';

const MODE_CONFIG = {
  issue: {
    phoneName: 'CooperationIssue',
    title: 'Kooperációs kiadás',
    successMessage: 'A kooperációs kiadás sikeresen rögzítve lett.',
    docTypeId: DocumentType.CooperationIssue,
  },
  return: {
    phoneName: 'CooperationReturn',
    title: 'Kooperációs bevételezés',
    successMessage: 'A kooperációs bevételezés sikeresen rögzítve lett.',
    docTypeId: DocumentType.CooperationReturn,
  },
};

const CooperationMaterialForm = ({ mode }) => {
  const operationInputRef = useRef(null);
  const itemCacheRef = useRef(new Map());
  const skipDocLinesResetRef = useRef(false);
  const { showLoader, hideLoader } = useLoader();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const { user, getUser } = useUsers();

  const [logInData, setLogInData] = useState({
    pinCode: '',
    userId: null,
  });
  const [showLogInDialog, setShowLogInDialog] = useState(true);

  const [operationIdentifier, setOperationIdentifier] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [note, setNote] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [targetDocType, setTargetDocType] = useState(null);
  const [isLoadingTargetDocType, setIsLoadingTargetDocType] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);

  const resolvedModeKey = mode === 'receipt' ? 'return' : mode;
  const modeConfig = MODE_CONFIG[resolvedModeKey] || MODE_CONFIG.issue;
  const isPartnerSelectionEnabled = params.COOPERATION_MATERIAL_PARTNER === 'Y';
  const isCooperationFromSourcelistEnabled = params.COOPERATION_FROM_SOURCELIST === 'Y';
  const cooperationTargetStatus =
    params.COOPERATION_TARGET_STATUS === DocStatus.OPENED ? DocStatus.OPENED : DocStatus.CLOSED;
  const isCooperationQtyFromPrevOpEnabled =
    resolvedModeKey === 'issue' && params.COOPERATION_QTY_FROM_PREV_OP === 'Y';

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();
    Object.keys(params).length === 0 && getParam();
    Object.keys(user).length === 0 && getUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadUserByPin = async () => {
      if (!logInData.pinCode) {
        setShowLogInDialog(true);
        return;
      }

      try {
        const data = await userService.getUserByPinCode(logInData.pinCode);
        if (cancelled) return;

        if (data && Object.keys(data).length > 0) {
          setLogInData((prev) => ({
            ...prev,
            userId: data.userId,
          }));
          setShowLogInDialog(false);
          return;
        }

        setLogInData({ pinCode: '', userId: null });
        setShowLogInDialog(true);
        addNotification({
          content: 'Nem található felhasználó ezzel a PIN kóddal!',
          type: NotificationType.ERROR,
        });
      } catch (e) {
        if (cancelled) return;
        setLogInData({ pinCode: '', userId: null });
        setShowLogInDialog(true);
        addNotification({
          content: 'Hiba a PIN kód ellenőrzésekor.',
          type: NotificationType.ERROR,
        });
      }
    };

    loadUserByPin();
    return () => {
      cancelled = true;
    };
  }, [logInData.pinCode]);

  useEffect(() => {
    let cancelled = false;

    const loadTargetDocType = async () => {
      setIsLoadingTargetDocType(true);
      try {
        const docType = await documentTypeService.getDocumentType(modeConfig.docTypeId);
        if (cancelled) return;

        setTargetDocType(docType);

        if (!String(docType?.defaultItemCode || '').trim()) {
          addNotification({
            content:
              'A célbizonylat bizonylattípusán nincs beállítva alapértelmezett cikk (defaultItemCode). Kérjük állítsd be, és próbáld újra.',
            type: NotificationType.ERROR,
          });
        }
      } catch (e) {
        if (cancelled) return;
        setTargetDocType(null);
        addNotification({
          content: 'Hiba a célbizonylat bizonylattípusának lekérésekor.',
          type: NotificationType.ERROR,
        });
      } finally {
        if (!cancelled) setIsLoadingTargetDocType(false);
      }
    };

    loadTargetDocType();
    return () => {
      cancelled = true;
    };
  }, [modeConfig.docTypeId]);

  useEffect(() => {
    let cancelled = false;

    const loadCompanyOptions = async () => {
      if (!isPartnerSelectionEnabled) {
        setSelectedCompany(null);
        setCompanyOptions([]);
        return;
      }

      showLoader();
      try {
        const companies = await masterDataService.companyToForm();
        if (cancelled) return;

        const filteredCompanies = (Array.isArray(companies) ? companies : []).filter(
          (company) => company?.active && company?.supplier && company?.cooperation
        );

        setCompanyOptions(filteredCompanies);
      } catch (e) {
        if (cancelled) return;
        addNotification({
          content: 'Hiba a kooperációs partnerek lekérésekor.',
          type: NotificationType.ERROR,
        });
      } finally {
        if (!cancelled) hideLoader();
      }
    };

    loadCompanyOptions();
    return () => {
      cancelled = true;
    };
  }, [isPartnerSelectionEnabled]);

  useEffect(() => {
    if (!isPartnerSelectionEnabled) return;
    if (skipDocLinesResetRef.current) {
      skipDocLinesResetRef.current = false;
      return;
    }
    setDocLines([]);
  }, [isPartnerSelectionEnabled, selectedCompany?.id]);

  const ensureDefaultItemCode = async () => {
    const cached = String(targetDocType?.defaultItemCode || '').trim();
    if (cached) return cached;

    try {
      const docType = await documentTypeService.getDocumentType(modeConfig.docTypeId);
      setTargetDocType(docType);

      const code = String(docType?.defaultItemCode || '').trim();
      if (!code) {
        addNotification({
          content: 'A célbizonylat bizonylattípusán nincs beállítva alapértelmezett cikk (defaultItemCode).',
          type: NotificationType.ERROR,
        });
        return null;
      }

      return code;
    } catch (e) {
      addNotification({
        content: 'Hiba a célbizonylat bizonylattípusának lekérésekor.',
        type: NotificationType.ERROR,
      });
      return null;
    }
  };

  const getItemDetailsByItemCode = async (itemCode) => {
    const code = String(itemCode || '').trim();
    if (!code) return null;

    const cached = itemCacheRef.current.get(code);
    if (cached) return cached;

    try {
      const res = await itemService.getItemById(code);
      const item = Array.isArray(res) ? res?.[0] : res;
      if (!item?.itemCode) return null;
      itemCacheRef.current.set(code, item);
      return item;
    } catch (e) {
      return null;
    }
  };

  const defaultDocumentLine = {
    lineStatus: cooperationTargetStatus === DocStatus.OPENED ? DocLineStatus.OPENED : DocLineStatus.CLOSED,
    itemCode: null,
    itemName: null,
    qty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    dueDate: dayjs(),
    internalNote: '',
    bpNote: '',
    docLineBatchSn: [],
    docLineBin: [],
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_int1: null,
    joker_int2: null,
    joker_int3: null,
  };

  const defaultValues = {
    docTypeId: modeConfig.docTypeId,
    docStatus: cooperationTargetStatus,
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: config?.defaultWarehouse || null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const getDefaultShippingAddress = (company) => {
    if (!company || !Array.isArray(company.companyAdress)) return null;

    return (
      company.companyAdress.find(
        (address) =>
          address?.active &&
          !address?.deletedBy &&
          address?.adressType === AddressType.SHIPPING &&
          address?.default
      ) ?? null
    );
  };

  const handleQtyChange = (id) => (e) => {
    const val = Math.max(0, Number(e.target.value));
    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, qty: val } : line)));
  };

  const handleRemoveLine = (id) => {
    setDocLines((prev) => prev.filter((line) => line.id !== id));
  };

  const addResolvedLine = (resolved, scannedIdentifier, defaultItemCode, defaultItem) => {
    // JOKER mezők töltése ebben a folyamatban (resolveOperationIdentifier válasz alapján):
    // - joker_char1: gyártási cikk cikkszám (resolved.production.articleNumber)
    // - joker_char2: gyártási cikk megnevezés (resolved.production.articleName)
    // - joker_char3: művelet név (resolved.operation.name)
    // - joker_char4: ME kód (resolved.production.quantityUnit.code)
    // - joker_int1: resolve rekord azonosító (resolved.id)
    // - joker_int2: ME azonosító (resolved.production.quantityUnit.id)
    // - joker_int3: gyártás azonosító (resolved.production.id)
    // - joker_char5: gyártás szám (resolved.production.workingNumber)
    // Megjegyzés: internalNote = beolvasott művelet azonosító (scannedIdentifier)
    const itemName = defaultItem?.itemName ?? null;

    const defaultItemQuantityUnit = defaultItem?.quantityUnit ?? null;
    const quantityUnitId = defaultItemQuantityUnit?.id ?? defaultItem?.quantityUnitId ?? null;
    const quantityUnitCode =
      String(defaultItemQuantityUnit?.code ?? defaultItem?.quantityUnitCode ?? '').trim() || null;
    const quantityUnitName =
      String(defaultItemQuantityUnit?.name ?? defaultItem?.quantityUnitName ?? '').trim() || null;

    const production = resolved?.production ?? null;
    if (!production) {
      addNotification({
        content: 'Hibás válasz: hiányzik a production objektum.',
        type: NotificationType.ERROR,
      });
      return;
    }

    const rawResolveId = resolved?.id ?? null;
    const parsedResolveId = rawResolveId === null ? null : Number(rawResolveId);
    const resolveId = Number.isFinite(parsedResolveId) ? parsedResolveId : null;

    const operation = resolved?.operation ?? null;
    const operationNameRaw = operation?.name ?? operation?.operationName ?? '';
    const operationName = String(operationNameRaw).trim() || null;

    const productionItemCode = String(production?.articleNumber ?? '').trim();
    const productionItemName = String(production?.articleName ?? '').trim();
    if (!productionItemCode) {
      addNotification({
        content: 'Hibás válasz: hiányzik a gyártási cikkszám (production.articleNumber).',
        type: NotificationType.ERROR,
      });
      return;
    }

    const productionQuantityUnit = production?.quantityUnit ?? null;
    const rawMeId = productionQuantityUnit?.id ?? production?.quantityUnitId ?? null;
    const parsedMeId = rawMeId === null ? null : Number(rawMeId);
    const meId = Number.isFinite(parsedMeId) ? parsedMeId : null;
    const meCode = String(productionQuantityUnit?.code ?? '').trim() || null;

    const rawProductionId = production?.id ?? null;
    const parsedProductionId = rawProductionId === null ? null : Number(rawProductionId);
    const productionId = Number.isFinite(parsedProductionId) ? parsedProductionId : null;
    const productionWorkingNumber = String(production?.workingNumber ?? '').trim() || null;

    const rawProductionQty = production?.productionQuantity;
    const parsedProductionQty = Number(rawProductionQty);
    const productionQty = Number.isFinite(parsedProductionQty) ? parsedProductionQty : 1;
    const rawCompletedQty = production?.completedQuantity;
    const parsedCompletedQty = Number(rawCompletedQty);
    const completedQty = Number.isFinite(parsedCompletedQty) ? parsedCompletedQty : null;
    const rawCalculatedQty = resolved?.calculatedQty;
    const parsedCalculatedQty = Number(rawCalculatedQty);
    const calculatedQty = Number.isFinite(parsedCalculatedQty) ? parsedCalculatedQty : null;
    const resolvedQty = isCooperationQtyFromPrevOpEnabled
      ? calculatedQty ?? completedQty ?? productionQty
      : productionQty;

    const newLine = {
      ...defaultDocumentLine,
      id: uuidv4(),
      itemCode: defaultItemCode,
      itemName,
      qty: Math.max(0, resolvedQty),
      whsCode: resolved?.production?.whsCode ?? config?.defaultWarehouse ?? resolved?.whsCode ?? null,
      quantityUnitId,
      quantityUnitCode,
      quantityUnitName,
      joker_char1: productionItemCode,
      joker_char2: productionItemName,
      joker_char3: operationName,
      joker_char4: meCode,
      joker_char5: productionWorkingNumber,
      joker_int1: resolveId,
      joker_int2: meId,
      joker_int3: productionId,
      internalNote: String(scannedIdentifier || '').trim(),
    };

    setDocLines((prev) => [...prev, newLine]);
  };

  const findFirstCooperationCompanyId = (value, visited = new Set()) => {
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) return null;
    if (visited.has(value)) return null;

    visited.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const foundValue = findFirstCooperationCompanyId(item, visited);
        if (foundValue !== null && foundValue !== undefined && foundValue !== '') {
          return foundValue;
        }
      }

      return null;
    }

    if ('cooperationCompanyId' in value) {
      const foundValue = value.cooperationCompanyId;
      if (foundValue !== null && foundValue !== undefined && foundValue !== '') {
        return foundValue;
      }
    }

    for (const nestedValue of Object.values(value)) {
      const foundValue = findFirstCooperationCompanyId(nestedValue, visited);
      if (foundValue !== null && foundValue !== undefined && foundValue !== '') {
        return foundValue;
      }
    }

    return null;
  };

  const tryApplyCooperationCompanyFromResolved = async (resolved) => {
    if (!isPartnerSelectionEnabled || !isCooperationFromSourcelistEnabled || selectedCompany) return;

    const cooperationCompanyId = findFirstCooperationCompanyId(resolved);
    if (!cooperationCompanyId) return;

    try {
      const company = await companyService.getCompanyById(cooperationCompanyId, true);
      if (company?.active && company?.supplier && company?.cooperation) {
        skipDocLinesResetRef.current = true;
        setSelectedCompany(company);
      }
    } catch (e) {
      return null;
    }

    return null;
  };

  const handleResolveOperation = async () => {
    const val = String(operationIdentifier || '').trim();

    if (!val) return;

    if (isLoadingTargetDocType) return;
    if (isResolving) return;
    if (isRegistering) return;

    if (docLines.some((l) => String(l?.internalNote || '').trim() === val)) {
      addNotification({
        content: 'Ez a művelet azonosító már hozzá lett adva a listához.',
        type: NotificationType.ERROR,
      });
      setOperationIdentifier('');
      setTimeout(() => {
        if (operationInputRef.current) operationInputRef.current.focus();
      }, 0);
      return;
    }

    const defaultItemCode = await ensureDefaultItemCode();
    if (!defaultItemCode) return;

    const defaultItem = await getItemDetailsByItemCode(defaultItemCode);
    if (!defaultItem?.itemCode) {
      addNotification({
        content: `Nincs találat az alapértelmezett cikkre: ${defaultItemCode}`,
        type: NotificationType.ERROR,
      });
      return;
    }

    setIsResolving(true);
    try {
      const resolved = await cooperationMaterialService.resolveOperationIdentifier({
        operationIdentifier: val,
        docTypeId: modeConfig.docTypeId,
      });

      if (!resolved) {
        addNotification({
          content: 'Nincs találat erre a művelet azonosítóra.',
          type: NotificationType.ERROR,
        });
        return;
      }

      await tryApplyCooperationCompanyFromResolved(resolved);
      addResolvedLine(resolved, val, defaultItemCode, defaultItem);
      setOperationIdentifier('');
      setTimeout(() => {
        if (operationInputRef.current) operationInputRef.current.focus();
      }, 0);
    } finally {
      setIsResolving(false);
    }
  };

  const handleRegister = async () => {
    if (isLoadingTargetDocType) return;
    if (isResolving) return;
    if (isRegistering) return;

    if (!logInData.userId) {
      setShowLogInDialog(true);
      addNotification({
        content: 'Nincs bejelentkezett felhasználó (PIN).',
        type: NotificationType.ERROR,
      });
      return;
    }

    const defaultItemCode = await ensureDefaultItemCode();
    if (!defaultItemCode) return;

    const errors = [];

    if (isPartnerSelectionEnabled && !selectedCompany) errors.push('Nincs kiválasztott kooperációs partner.');
    if (docLines.length === 0) errors.push('Nincs hozzáadott tétel.');
    if (docLines.some((line) => Number(line.qty) <= 0))
      errors.push('Minden mennyiségnek 0-nál nagyobbnak kell lennie.');

    if (errors.length) {
      addNotification({
        content: errors.join('\n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const selectedCompanyDocumentFields = selectedCompany
      ? {
          companyId: selectedCompany.id ?? null,
          companyCode: selectedCompany.code ?? '',
          companyName: selectedCompany.name ?? '',
          companyContactId: selectedCompany.companyContact?.find((contact) => contact.default)?.id ?? null,
          shortName: selectedCompany.shortName ?? '',
          incotermCity: selectedCompany.incotermCity ?? '',
          companyWarning: selectedCompany.companyWarning ?? '',
          currencyCode: selectedCompany.supplierCurrencyCode || config.currencyCode || null,
          paymentMethodId: selectedCompany.supplierPaymentMethodId ?? null,
          salesOrPurchasePersonId: selectedCompany.purchasePersonId ?? null,
          deliveryMethodId: selectedCompany.supplierDeliveryMethodId ?? null,
          incotermCode: selectedCompany.supplierIncotermCode ?? null,
          priceListId: selectedCompany.supplierPriceListId ?? null,
          langCode: selectedCompany.languageCode ?? null,
          hunTaxNumber: selectedCompany.hunTaxNumber ?? null,
          euTaxNumber: selectedCompany.euTaxNumber ?? null,
          foreignTaxNumber: selectedCompany.foreignTaxNumber ?? null,
        }
      : null;

    const selectedCompanyShippingAddress =
      resolvedModeKey === 'issue' ? getDefaultShippingAddress(selectedCompany) : null;

    const doc = {
      ...cleanValues(defaultValues),
      ...(selectedCompanyDocumentFields || {}),
      ...(selectedCompanyShippingAddress
        ? {
            shippingAdressId: selectedCompanyShippingAddress.id ?? null,
            shippingCountry: selectedCompanyShippingAddress.country?.name ?? '',
            shippingProvince: selectedCompanyShippingAddress.province ?? '',
            shippingPostCode: selectedCompanyShippingAddress.postCode ?? '',
            shippingCity: selectedCompanyShippingAddress.city ?? '',
            shippingStreetAddress: selectedCompanyShippingAddress.streetAddress ?? '',
          }
        : {}),
      note,
      // include createdBy/reportedBy on each line to ensure PIN user is carried to backend
      docLine: docLines.map((line, index) =>
        cleanValues({
          ...line,
          createdBy: logInData.userId,
          reportedBy: logInData.userId,
          visOrder: index + 1,
        })
      ),
      createdBy: logInData.userId,
    };

    setIsRegistering(true);
    try {
      await cooperationMaterialService.createDocument(doc);

      addNotification({
        content: modeConfig.successMessage,
        type: NotificationType.SUCCESS,
      });

      setDocLines([]);
      setNote('');
      setOperationIdentifier('');
      if (isPartnerSelectionEnabled) {
        setSelectedCompany(null);
      }

      setTimeout(() => {
        if (operationInputRef.current) operationInputRef.current.focus();
      }, 0);
    } catch (e) {
      addNotification({
        content: 'Hiba a rögzítés során.',
        type: NotificationType.ERROR,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const isValid =
    (!isPartnerSelectionEnabled || !!selectedCompany) &&
    docLines.length > 0 &&
    docLines.every((line) => Number(line.qty) > 0);

  const companyAutocompleteOptions = generateAutocompleteItems(companyOptions, ['code', 'name'], 'id');
  const companyAutocompleteHelperText =
    companyAutocompleteOptions.length === 0 ? 'Nincs választható kooperációs partner.' : '';

  const handleSelectCompany = async (companyId) => {
    if (!companyId) {
      setSelectedCompany(null);
      setTimeout(() => {
        if (operationInputRef.current) operationInputRef.current.focus();
      }, 0);
      return;
    }

    try {
      const company = await companyService.getCompanyById(companyId, true);
      setSelectedCompany(company);
      setTimeout(() => {
        if (operationInputRef.current) operationInputRef.current.focus();
      }, 0);
    } catch (e) {
      addNotification({
        content: 'Hiba a kooperációs partner betöltésekor.',
        type: NotificationType.ERROR,
      });
    }
  };

  return (
    <PhoneBase name={modeConfig.phoneName} sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {modeConfig.title}
      </Typography>

      {isPartnerSelectionEnabled ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h7" component="h4">
              Kooperációs partner
            </Typography>
          </Box>

          <Box sx={{ mt: 1, bgcolor: 'white' }}>
            <AutoCompleteSelectUI
              placeholder="Kooperációs partner kiválasztása"
              size="small"
              variant="outlined"
              selectedValue={selectedCompany?.id ?? null}
              selectedLabelValue={
                companyAutocompleteOptions.find((company) => company.value === selectedCompany?.id)?.label ?? ''
              }
              dataset={companyAutocompleteOptions}
              disabled={isResolving || isRegistering}
              onChange={(_e, newVal, reason) => {
                if (reason === 'clear' || !newVal?.value) {
                  handleSelectCompany(null);
                  return;
                }

                handleSelectCompany(newVal.value);
              }}
            />
          </Box>

          {companyAutocompleteHelperText ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {companyAutocompleteHelperText}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          Művelet azonosító
        </Typography>

        <Box sx={{ mt: 1, bgcolor: 'white' }}>
          <InputUI
            autoFocus
            fullWidth
            size="small"
            placeholder="Művelet azonosító beolvasása"
            value={operationIdentifier}
            inputRef={operationInputRef}
            onChange={(e) => setOperationIdentifier(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleResolveOperation();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="Feldolgozás"
                    onClick={handleResolveOperation}
                    disabled={isResolving}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          Tételek
        </Typography>

        <Stack spacing={0.25} sx={{ mt: 1.5 }}>
          {docLines.map((line) => {
            const key = line.id;

            const productionItemCode = String(line.joker_char1 || '').trim();
            const productionItemName = String(line.joker_char2 || '').trim();
            const productionLabel = productionItemCode
              ? `${productionItemCode}${productionItemName ? ` - ${productionItemName}` : ''}`
              : '-';

            const operationName = String(line.joker_char3 || '').trim();

            return (
              <Box
                key={key}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white',
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                      {productionLabel}
                    </Typography>

                    {line.internalNote ? (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                        Művelet: {line.internalNote}
                      </Typography>
                    ) : null}

                    {operationName ? (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                        Művelet név: {operationName}
                      </Typography>
                    ) : null}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InputUI
                      name="qty"
                      type="number"
                      size="small"
                      sx={{ width: 110 }}
                      inputProps={{ min: 0, step: 1 }}
                      value={line.qty ?? 0}
                      onChange={handleQtyChange(key)}
                    />
                    <IconButton color="error" size="small" aria-label="Törlés" onClick={() => handleRemoveLine(key)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          Megjegyzés
        </Typography>

        <Box sx={{ mt: 1, bgcolor: 'white' }}>
          <InputUI
            fullWidth
            multiline
            minRows={3}
            placeholder="Megjegyzés"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Box>
      </Box>

      <Box textAlign="center" mt="2rem">
        <ButtonUI
          text="Rögzítés"
          className="bg-success"
          onClick={handleRegister}
          disabled={!isValid || isResolving || isRegistering || !logInData.userId}
        />
      </Box>

      <PinCodeLogInDialog
        open={showLogInDialog}
        handleClose={() => setShowLogInDialog(false)}
        logInData={logInData}
        setLogInData={setLogInData}
      />
    </PhoneBase>
  );
};

export default CooperationMaterialForm;
